import { createContext, useContext, useMemo } from 'react'

import { useAuth } from './useAuth'
import {
  displayToMetric,
  formatMeasurement,
  labelWithUnit,
  metricToDisplay,
  normalizeMeasurementUnit,
  parseMeasurementInput,
  unitLabelFor,
  type MeasurementKind,
  type MeasurementUnit
} from '../lib/measurements'

const MeasurementUnitContext = createContext<MeasurementUnit>('Metric')

export const MeasurementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const unit = normalizeMeasurementUnit(user?.measurementUnit)

  return (
    <MeasurementUnitContext.Provider value={unit}>
      {children}
    </MeasurementUnitContext.Provider>
  )
}

export const useMeasurements = () => {
  const unit = useContext(MeasurementUnitContext)

  return useMemo(
    () => ({
      unit,
      isImperial: unit === 'Imperial',
      unitLabel: (kind: MeasurementKind) => unitLabelFor(kind, unit),
      label: (label: string, kind: MeasurementKind) => labelWithUnit(label, kind, unit),
      format: (
        valueMetric: number | null | undefined,
        kind: MeasurementKind,
        options?: Parameters<typeof formatMeasurement>[3]
      ) => formatMeasurement(valueMetric, kind, unit, options),
      toDisplay: (valueMetric: number, kind: MeasurementKind) => metricToDisplay(valueMetric, kind, unit),
      toMetric: (valueDisplay: number, kind: MeasurementKind) => displayToMetric(valueDisplay, kind, unit),
      parseInput: (value: string, kind: MeasurementKind) => parseMeasurementInput(value, kind, unit)
    }),
    [unit]
  )
}

export type { MeasurementKind, MeasurementUnit }
